import React, { createContext, useState, useContext } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const baseThemeOptions = {
  shape: {
    borderRadius: 14,
  },
  custom: {
    gradients: {
      appBackgroundDark: 'radial-gradient(1200px circle at 10% 10%, rgba(187, 134, 252, 0.18), transparent 45%), radial-gradient(900px circle at 90% 20%, rgba(3, 218, 198, 0.16), transparent 45%), linear-gradient(135deg, #070710 0%, #0B0B12 50%, #0D0221 100%)',
      appBackgroundLight: 'linear-gradient(135deg, #f6f7fb 0%, #eef2ff 50%, #f5f7fa 100%)',
      sectionPrimaryDark: 'linear-gradient(135deg, rgba(187, 134, 252, 0.18) 0%, rgba(3, 218, 198, 0.12) 50%, rgba(29, 12, 64, 0.7) 100%)',
      sectionPrimaryLight: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      brand: 'linear-gradient(90deg, #BB86FC 0%, #03DAC6 100%)',
      brandTextDark: 'linear-gradient(135deg, #BB86FC 0%, #7C4DFF 45%, #03DAC6 100%)',
    },
    glass: {
      bgDark: 'rgba(255, 255, 255, 0.06)',
      bgLight: 'rgba(255, 255, 255, 0.85)',
      borderDark: '1px solid rgba(255, 255, 255, 0.10)',
      borderLight: '1px solid rgba(0, 0, 0, 0.06)',
      blur: 'blur(14px)',
    },
    shadows: {
      softDark: '0 16px 40px rgba(0, 0, 0, 0.55)',
      softLight: '0 16px 40px rgba(0, 0, 0, 0.12)',
      glowPrimary: '0 0 0 1px rgba(187, 134, 252, 0.16), 0 12px 40px rgba(187, 134, 252, 0.18)',
      glowSecondary: '0 0 0 1px rgba(3, 218, 198, 0.14), 0 12px 40px rgba(3, 218, 198, 0.12)',
    },
  },
  typography: {
    fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 750, letterSpacing: '-0.015em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 650 },
    h6: { fontWeight: 650 },
    button: { textTransform: 'none', fontWeight: 650 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
  },
};

const lightTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: '#1e3c72',
    },
    secondary: {
      main: '#2a5298',
    },
    background: {
      default: '#f6f7fb',
      paper: '#ffffff',
    },
  },
});

const darkTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: '#BB86FC',
    },
    secondary: {
      main: '#03DAC6',
    },
    info: {
      main: '#2DE2E6',
    },
    success: {
      main: '#00E676',
    },
    warning: {
      main: '#FFB74D',
    },
    error: {
      main: '#FF4D6D',
    },
    background: {
      default: '#0B0B12',
      paper: '#12121B',
    },
    text: {
      primary: '#F2F4FF',
      secondary: 'rgba(242, 244, 255, 0.72)',
    },
  },
});

const ThemeContext = createContext();

export const ThemeContextProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(true);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);
